import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
// Hooks
import {
  useGetUserTypeFromRoute,
  useDefaultStoreRedirectOnEmptyId,
} from 'hooks';
// Api
import { CREATE_STREAM_ADMIN } from 'api/streams/mutations';
import { GET_MODERATORS } from 'api/streams/queries';
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
// Types
import {
  CreateStreamAdmin,
  CreateStreamAdminVariables,
} from 'api/streams/types/CreateStreamAdmin';
import { GetModerators } from 'api/streams/types/GetModerators';
import {
  CreateMentionInput,
  CreateUnregisteredContactInput,
} from 'api/graphql-global-types';
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import { Streams_adminStreams_entities_mentions } from 'api/streams/types/Streams';
// Helpers
import { getTimeZoneName, toLegacyScheduleDateString } from 'helpers/timeZone';
import { formatHashtagInput } from 'helpers/hashtags';
import { formatMentionsInput } from 'helpers/mentions';
import { getProperErrorMessage } from 'helpers/errors';
// Routes
import {
  MANAGE_ATHLETE_SCHEDULED_STREAMS_GEN,
  MANAGE_CONTENT_CREATOR_SCHEDULED_STREAMS_GEN,
  MANAGE_ORGANIZATION_SCHEDULED_STREAMS_GEN,
} from 'constants/routes';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
import { MentionValues } from 'uiShared/Mentions/Mentions';
// Components
import { CreateEditStreamForm } from 'components/common/CreateEditStreamForm/CreateEditStreamForm';

const SetupStream = (): JSX.Element => {
  useDefaultStoreRedirectOnEmptyId();

  const { isAthlete, isContentCreator, isOrganization } =
    useGetUserTypeFromRoute();
  const history = useHistory();
  const { storeId } = useParams<{ storeId: string }>();
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  const { data } = useQuery<GetModerators>(GET_MODERATORS);

  const [createStream, { loading }] = useMutation<
    CreateStreamAdmin,
    CreateStreamAdminVariables
  >(CREATE_STREAM_ADMIN);

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const handleStreamCreate = async (values: any) => {
    const timeZoneOffset = momentTimezone(values.date)
      .tz(getTimeZoneName(values.tzCode))
      .utcOffset();

    const sponsorInput =
      !values?.sponsorImage || !values?.sponsorName
        ? []
        : [
            {
              name: values?.sponsorName,
              imageFile:
                process.env.NODE_ENV === 'test'
                  ? null
                  : values?.sponsorImage?.[0]?.originFileObj,
              promoMessage: values?.sponsorPromoMessage,
            },
          ];

    const createUnregisteredContactInput =
      (values.mentions as MentionValues[])?.filter(
        (mention) => !mention?.targetType
      ) || [];

    const unregisterContactsData = await createUnregisteredContact({
      variables: {
        input:
          createUnregisteredContactInput as CreateUnregisteredContactInput[],
      },
    });

    const newUnregisteredContacts =
      (unregisterContactsData?.data?.createUnregisteredContact || [])?.map(
        (contact) => ({
          unregisteredContactId: contact?.id,
        })
      ) || [];

    const oldMentions =
      values.mentions?.filter(
        (mention: MentionValues) => mention?.targetType
      ) || [];
    const formatedOldMentions = formatMentionsInput(
      oldMentions as Streams_adminStreams_entities_mentions[]
    );

    try {
      await createStream({
        variables: {
          input: {
            storeId,
            scheduleDate: toLegacyScheduleDateString(
              moment(values.date).startOf('minute').toDate()
            ),
            timeZone: {
              tzCode: values.tzCode,
              offset: timeZoneOffset,
            },
            mentionsInputs: [
              ...formatedOldMentions,
              ...newUnregisteredContacts,
            ] as CreateMentionInput[],
            hashtagInputs: values?.hashtags?.length
              ? formatHashtagInput(values.hashtags)
              : null,
            image:
              process.env.NODE_ENV === 'test'
                ? null
                : values?.picture?.[0]?.originFileObj,
            name: values?.repeatsEveryEnum ? null : values?.name || '',
            repeatsEveryEnum: values?.repeatsEveryEnum,
            repeatingTitle: values?.repeatsEveryEnum ? values.name : null,
            price: !values?.isFree ? values?.price : null,
            isFree: Boolean(values?.isFree),
            postEventContentHubEnabled: !values?.isFree,
            isHidden: Boolean(values?.isHidden),
            chatEnabled:
              values?.chatEnabled !== undefined ? values.chatEnabled : true,
            description: values?.description || '',
            sponsors: sponsorInput,
            moderatorId: values?.moderatorId || null,
            affiliateProducts: values?.affiliateProducts || [],
            participantIds: selectedStoreIds,
          },
        },
      });

      successNotification('The stream has been created successfully');

      if (isAthlete) {
        history.push(`${MANAGE_ATHLETE_SCHEDULED_STREAMS_GEN}/${storeId}`);
      } else if (isOrganization) {
        history.push(`${MANAGE_ORGANIZATION_SCHEDULED_STREAMS_GEN}/${storeId}`);
      } else if (isContentCreator) {
        history.push(
          `${MANAGE_CONTENT_CREATOR_SCHEDULED_STREAMS_GEN}/${storeId}`
        );
      } else {
        history.push('/');
      }
    } catch (err: any) {
      errorNotification(
        getProperErrorMessage(
          err,
          `Something went wrong.${err?.message ? ` (${err?.message})` : ''}`
        )
      );
    }
  };

  return (
    <CreateEditStreamForm
      selectedStoreIds={selectedStoreIds}
      setSelectedStoreIds={setSelectedStoreIds}
      ownerStoreId={storeId}
      onFinish={handleStreamCreate}
      loading={loading}
      submitButtonTitle="Create Stream"
      moderators={data?.moderators?.entities}
    />
  );
};

export default SetupStream;
